import { Component, ElementRef, OnInit, inject, signal, computed } from '@angular/core';
import { AdminAreaListComponent, ListObject } from './admin-area-list/admin-area-list.component';
import { UserService } from '../services/api-services/user.service';
import { RoleToStringMapping } from '../utils/models/tool.models';
import { ToolService } from '../services/api-services/tools/tool.service';
import { ToolchainService } from '../services/api-services/toolchains/toolchain.service';
import { MatDialog } from '@angular/material/dialog';
import { GeneralService } from '../services/api-services/general.service';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { UserConfigComponent } from './user-config/user-config.component';
import { ToolInstallationComponent } from './tool-installation/tool-installation.component';
import { ToolchainConfigComponent } from './toolchain-config/toolchain-config.component';
import { ToolchainInstallationComponent } from './toolchain-installation/toolchain-installation.component';
import { ToolConfigComponent } from './tool-config/tool-config.component';
import { resourceValueOr } from '../utils/resource';

export enum TabView {
  List = 'list',
  Config = 'config',
  Installation = 'installation',
}

@Component({
  selector: 'app-admin-area',
  templateUrl: './admin-area.component.html',
  styleUrls: ['./admin-area.component.scss'],
  imports: [
    MatTabGroup,
    MatTab,
    AdminAreaListComponent,
    UserConfigComponent,
    ToolInstallationComponent,
    ToolchainConfigComponent,
    ToolchainInstallationComponent,
    ToolConfigComponent,
  ],
})
export class AdminAreaComponent implements OnInit {
  /** Forward to the template. */
  protected readonly TabView = TabView;

  /** Inject services. */
  protected readonly dialogRef = inject(MatDialog);
  private readonly userService = inject(UserService);
  private readonly toolService = inject(ToolService);
  private readonly toolchainService = inject(ToolchainService);
  private readonly generalService = inject(GeneralService);
  private readonly elementRef = inject(ElementRef);

  protected readonly tools = this.toolService.getAllToolsAdmin();
  protected readonly users = this.userService.getAllUsers();
  protected readonly toolchains = this.toolchainService.getToolchainDescriptionsAdmin();
  protected readonly registrations = this.toolService.getRegistrations();

  userList = computed<ListObject[]>(() => {
    const users = this.users.value() ?? [];
    const registrations = this.registrations.value() ?? [];
    return users.map(user => {
      const usedTools = registrations.filter(x => x.user.userId == user.userId).map(r => r.tool);
      let formattedUserTools = '';

      if (usedTools.length > 0) {
        for (let i = 0; i < usedTools.length - 1; i++) {
          formattedUserTools += usedTools[i].name + ', ';
        }
        formattedUserTools += usedTools[usedTools.length - 1].name;
      }

      return {
        id: user.userId,
        icon: 'account_box',
        title: user.userName,
        subtitle: formattedUserTools,
        additional_text:
          RoleToStringMapping[user.role] + ` (Chat ${user.canAccessChat ? '✓' : '✗'})`,
      };
    });
  });

  toolList = computed<ListObject[]>(
    () =>
      this.tools.value()?.map(tool => ({
        id: tool.toolId,
        icon: 'construction',
        title: tool.name,
        subtitle: tool.project,
        additional_text: tool.author,
      })) ?? []
  );

  toolchainList = computed<ListObject[]>(
    () =>
      this.toolchains.value()?.map(toolchain => ({
        id: toolchain.toolchainId,
        icon: 'precision_manufacturing',
        title: toolchain.displayName ?? toolchain.name,
        subtitle: toolchain.project,
        additional_text: toolchain.author,
      })) ?? []
  );

  userView = signal(TabView.List);
  selectedUser = signal<number | undefined>(undefined);

  toolView = signal(TabView.List);
  selectedTool = signal<number | undefined>(undefined);

  toolchainView = signal(TabView.List);
  selectedToolchain = signal<number | undefined>(undefined);

  hepVersionFrontend = resourceValueOr(this.generalService.getFrontendVersion(), 'local');
  hepVersionBackend = this.generalService.getBackendVersion();

  ngOnInit() {
    this.elementRef.nativeElement
      .querySelector('.mat-mdc-tab-header')
      .addEventListener('click', this.closeConfig.bind(this));
  }

  openUserConfig(userId: number) {
    const users = this.users.value();
    if (userId == -1) {
      const previousUserId =
        users?.reduce(function (prev, current) {
          return prev && prev.userId > current.userId ? prev : current;
        }).userId ?? 0;
      this.selectedUser.set(previousUserId + 1);
    } else {
      this.selectedUser.set(userId);
    }
    this.userView.set(TabView.Config);
  }

  openToolConfig(toolId: number) {
    if (toolId == -1) {
      this.toolView.set(TabView.Installation);
    } else {
      this.selectedTool.set(toolId);
      this.toolView.set(TabView.Config);
    }
  }

  openToolchainConfig(toolchainId: number) {
    if (toolchainId == -1) {
      this.toolchainView.set(TabView.Installation);
    } else {
      this.toolchainView.set(TabView.Config);
      this.selectedToolchain.set(toolchainId);
    }
  }

  closeConfig() {
    this.userView.set(TabView.List);
    this.toolView.set(TabView.List);
    this.toolchainView.set(TabView.List);
    this.tools.reload();
    this.users.reload();
    this.toolchains.reload();
    this.registrations.reload();
  }
}
